import clsx from "clsx";
// Redux
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
// React Query
import { useMutation, useQueryClient } from "@tanstack/react-query";

// MUI
import { Button, Stack, Typography } from "@mui/material";

// API
import { transferGPSDevice } from "@DataPages/api/inventory/mutations";

// Hooks
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";
import { useCommissionCyclesData } from "@Washouts/hooks/common/useCommissionCyclesData";
import { useInventoryContext } from "@DataPages/components/Inventory/contexts/InventoryFilterContext";

// Helpers
import { and, or } from "@Common/helpers/sonar";

// Constants
import actionsList from "@Constants/actionsList";

// Types
import { ISettingsAndConfigurations } from "@Common/interfaces/configurations";

type TransferringGPSDeviceProps = Readonly<{
	inventoryId: string;
	gpsInconsistency?: boolean;
}>;

export default function TransferGPSDeviceSection(props: TransferringGPSDeviceProps) {
	const { useAuth } = useAuthorization();
	const queryClient = useQueryClient();

	const location = useSelector((state: RootState) => state.location.location);
	if (!location) {
		return null;
	}
	const { name, locationId } = location;

	const { commissionCycleId: selectedCCId, payPeriodId: selectedPPId } = useInventoryContext();
	const { commissionCycle: actualCC, payPeriodByDate: actualPP } = useCommissionCyclesData({ date: new Date() });

	const { administrationTools } = queryClient.getQueryData<ISettingsAndConfigurations>(["settingsAndConfigurations", locationId]) ?? {};
	const providerDBProperty = Object.keys(administrationTools?.integrations?.globalPositioningService ?? {}).find(
		(key) => !["_id", "lots"].includes(key)
	);

	/********************************************* MUTATIONS ************************************************/
	const { mutate: transferGPSDeviceMutation, isPending } = useMutation({
		mutationKey: ["transferGPSDevice", locationId, props.inventoryId],
		mutationFn: () => transferGPSDevice({ locationId, inventoryId: props.inventoryId, eventSource: "CLIENT" }),
	});

	/********************************************* HELPERS ************************************************/
	const isTransferEnabled = (): boolean => {
		const hasTransferGPSDevicePermission = useAuth(actionsList.VIEW_GPS_EMBEDDED_URL_ADMIN_USER);

		const isSelectedCommissionCycleActual = actualCC?._id === selectedCCId;
		const isSelectedPayPeriodActual = actualPP?._id === selectedPPId;

		return hasTransferGPSDevicePermission && props.gpsInconsistency && isSelectedCommissionCycleActual && isSelectedPayPeriodActual;
	};

	/********************************************* HANDLERS ************************************************/
	const handleTransferDevice = () => {
		transferGPSDeviceMutation();
	};

	return (
		<Stack spacing={1.5}>
			<Typography className={"!font-montserrat !font-medium !text-sm !text-default"}>{name}</Typography>
			<Button
				variant={"contained"}
				onClick={handleTransferDevice}
				disabled={or(isPending, !providerDBProperty, !isTransferEnabled())}
				classes={{
					root: clsx({
						"!h-[30px] !text-white !normal-case !font-sans !font-normal !rounded-md !w-[200px]": true,
						"!bg-primary": and(!isPending, isTransferEnabled(), Boolean(providerDBProperty)),
					}),
					disabled: "!bg-disabled",
				}}
				disableElevation
				sx={{
					"&:hover": {
						boxShadow: "0px 0px 6px 2px rgba(35,87,190,0.5)",
					},
				}}
			>
				Transfer Device to This Location
			</Button>
		</Stack>
	);
}
